import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'
import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useMainNavigation from '@hooks/useMainNavigation'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import BulkAssignUserTasksModal from '@modals/BulkAssignUserTasksModal'
import CreateOrEditTaskListItemModal from '@modals/CreateOrEditTaskListItemModal'
import ManageTaskListItemsModal from '@modals/ManageTaskListItemsModal'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    resetFilters: () => void,
    showBulkAssignUserTasksModal: (payload: object) => void,
    showCreateOrEditTaskListItemModal: () => void,
    showManageTaskListItemsModal: (payload: object) => void,
    updateFilters: () => void,
  },
  defaultFilters: object,
}

type DefaultState = {
  showBulkAssignUserTasksModal: boolean,
  showCreateOrEditTaskListItemModal: boolean,
  showManageTaskListItemsModal: boolean,}

const defaultState = {
  showBulkAssignUserTasksModal: false,
  showCreateOrEditTaskListItemModal: false,
  showManageTaskListItemsModal: false,
}

const callbacks = (component: string, setState: (state: Partial<DefaultState>) => void) => {
  const componentCallbacks = {
    BulkAssignUserTasksModal: {
      closeModal: () => setState({ showBulkAssignUserTasksModal: false }),
      bulkAssignUserTasks: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditTaskListItemModal: {
      closeModal: () => setState({ showCreateOrEditTaskListItemModal: false }),
      createTaskListItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteTaskListItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTaskListItem: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageTaskListItemsModal: {
      closeModal: () => setState({ showManageTaskListItemsModal: false }),
      bulkUpdateTaskListItems: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const Overview = () => {
  const [state, setState] = useSetState(defaultState)
  const { showBulkAssignUserTasksModal, showCreateOrEditTaskListItemModal, showManageTaskListItemsModal } = state

  const { callbacks: { toggleNavigation } } = useMainNavigation()

  const filtersPayload = useFilters({
    persistFilters: true,
  })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const { task_list_id, task_list_section_id } = pageFilters

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showBulkAssignUserTasksModal: (payload: object) => {
        setModalData('BulkAssignUserTasksModal', payload)
        setState({ showBulkAssignUserTasksModal: true })
      },
      showCreateOrEditTaskListItemModal: () => setState({ showCreateOrEditTaskListItemModal: true }),
      showManageTaskListItemsModal: (payload: { taskListItemIds: number[] }) => {
        setModalData('ManageTaskListItemsModal', payload)
        setState({ showManageTaskListItemsModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <BulkAssignUserTasksModal
          callbacks={callbacks('BulkAssignUserTasksModal', setState)}
          requestOptions={{
            include_image_sizes: true,
            include_related_entity_subject: true,
            include_task_list_item_assignee: true,
            include_task_list_item_parent_task: true,
            include_task_list_item_related_entities: true,
            include_task_list_item_task_list: true,
            include_user_image: true,
          }}
          showModal={showBulkAssignUserTasksModal}
        />

        <CreateOrEditTaskListItemModal
          callbacks={callbacks('CreateOrEditTaskListItemModal', setState)}
          showModal={showCreateOrEditTaskListItemModal}
          taskListItem={{ task_list_id, task_list_section_id, task_type: 'new' }}
        />

        <ManageTaskListItemsModal
          callbacks={callbacks('ManageTaskListItemsModal', setState)}
          showModal={showManageTaskListItemsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Overview
